import styled, { css } from 'styled-components';

/**
 * The pieces more than one chat component wears.
 *
 * Everything else in the feature keeps its styles in a `*.styled.tsx` beside
 * the component that uses them; this file is only for what genuinely has two or
 * more callers, so "where does this live" has one answer: next to its component
 * unless it is here.
 *
 * A leaf module - styled-components and the theme, nothing else - so importing
 * it can never close an import cycle.
 */

/**
 * The thin scrollbar every bounded chat surface uses: the conversation, the
 * mention menu, the admin inbox column and the member pickers. The default OS
 * bar reads as a slab across the rows at these widths.
 */
export const thinScrollbar = css`
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.mediumGray};
    border-radius: 3px;
  }
`;

/**
 * The composer's surface and its typography, kept as fragments so anything else
 * that has to look like the message field (the inbox search, both member
 * searches) wears the same values instead of a copy that drifts.
 *
 * 40px tall: 32px of content plus the bar's own 4px above and below.
 */
const composerSurface = css`
  display: flex;
  gap: 8px;
  padding: 4px 4px 4px 16px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
`;

const composerText = css`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.darkGray};
  padding: 6px 0;

  ::placeholder {
    color: ${({ theme }) => theme.lightGray};
  }
`;

/**
 * Wraps the composer and everything stacked above it - the reply strip, staged
 * files, the mention menu - and is what the recorder replaces the bar inside.
 */
export const ComposerWrapper = styled.div`
  /* Matches ThreadHeaderSlot: the same inset on all three free sides. */
  padding: 16px;
  /* The fade ModalFooter lays over a scrollable modal body, but with no solid
     plateau: an even ramp from opaque at the card's bottom edge to nothing just
     above the bar. A stop partway up puts a visible band across the thread,
     which is exactly what a fade is meant to avoid - and a message is supposed
     to still show faintly beside the bar on its way out.

     The colour comes from --chat-fade, published by ChatThreadWrapper, because
     the conversation scrolls over a white card in one app and the page's cream
     in the other. The literals are the fallback for anything that wears this
     bar outside a thread. */
  background: linear-gradient(
    to top,
    var(--chat-fade, #ffffff) 0%,
    var(--chat-fade-end, #ffffff00) 100%
  );

  /* 12 on every side, the same as ThreadHeaderSlot at the other end of the
     conversation - the bar and the title above it have to sit the same distance
     off the screen's edges or the thread looks tilted. It used to be 16 at the
     sides, which drew the composer visibly narrower than the header. */
  @media (max-width: 800px) {
    padding: 12px;
  }

  /* Installed app on a phone: no browser toolbar sits under the bar, so 12px
     leaves it on the screen edge, level with the home indicator. A browser tab
     needs nothing - its own chrome is already the gap.

     env() only reports a real inset under viewport-fit=cover, which these
     apps do not set, so the literal is what does the work today; the inset is
     there to take over if that ever changes. */
  @media (display-mode: standalone) and (max-width: 800px) {
    padding-bottom: max(24px, calc(12px + env(safe-area-inset-bottom, 0px)));
  }
`;

export const ComposerBar = styled.div`
  ${composerSurface}
  /* Bottom-aligned so the icons stay level with the last line as the draft
     grows; the search bar below centres instead, having only one line. */
  align-items: flex-end;

  /*
   * The right end keeps the same 4px the top and bottom have, phone or not.
   *
   * It was widened to 10 on the theory that a rounded end curves away from a
   * filled disc parked in it. It does not: the bar's radius is half its own
   * height, so the end IS a semicircle, and a 40px disc 4px in from it is
   * concentric with that semicircle - a even 4px ring the whole way round.
   * Once the draft wraps and the bar grows, the disc is bottom-aligned into a
   * 24px corner and comes out concentric with that too.
   *
   * The left stays at 16: that side is text against a straight run, where 16 is
   * the reading margin rather than a ring around a circle.
   */
  @media (max-width: 800px) {
    /*
     * Attach and mic close up into one cluster.
     *
     * They grow to 40px here while their glyphs stay at 24, so each already
     * carries 8px of its own air on every side - the bar's 8px gap on top of
     * that pushes them 24px apart and they stop reading as a pair of related
     * controls. Zero gap still leaves 16px between the glyphs themselves.
     *
     * Only between the two ghost buttons: excluding the last child is what
     * keeps the send disc out of it. That one is filled, so it needs the gap
     * to sit apart from the icons rather than run into them.
     */
    > button + button:not(:last-child) {
      margin-left: -8px;
    }
  }
`;

export const ComposerIconButton = styled.button`
  flex: none;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  :disabled {
    cursor: not-allowed;
  }

  :hover:not(:disabled) {
    background: ${({ theme }) => theme.gray};
  }

  /* Bigger on a phone, not smaller. A 32px target sized for a mouse is under
     the 44px a thumb needs, and the glyphs inside it read as decoration next to
     a full-width input - which is the one place they have to look pressable. */
  @media (max-width: 800px) {
    width: 40px;
    height: 40px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

/**
 * Send: a filled disc rather than a bare glyph like the attach and mic buttons
 * beside it.
 *
 * It is the bar's one primary action, and it carries its own state - grey means
 * there is nothing to send, red means there is - so it has to read as a button
 * whether or not you are pointing at it.
 *
 * `theme.mediumGray` is the border grey and would leave a white arrow on a
 * near-white disc; `lightGray` is the one that actually holds a white glyph.
 *
 * No hover, deliberately: the disc is already filled, so the inherited grey
 * wash has nothing to reveal, and tinting a red button on hover only makes it
 * look half-pressed. State comes from the `disabled` attribute the caller
 * already sets, not from a second prop that could disagree with it.
 */
export const SendButton = styled(ComposerIconButton)`
  background: ${({ theme }) => theme.primary};

  :hover:not(:disabled) {
    background: ${({ theme }) => theme.primary};
  }

  :disabled {
    background: ${({ theme }) => theme.lightGray};
  }

  /* Smaller than the icon's own 20px: inside a filled disc the arrow needs
     room around it or the disc reads as a badge rather than a button. Not as
     small as the record button's 12px mic, though - the paper plane is a
     sparse diagonal shape and goes thin and timid well before a solid glyph
     would. */
  svg {
    width: 16px;
    height: 16px;
  }

  /* Follows the bigger disc it inherits, keeping the same air around the
     arrow - the parent's mobile rule would otherwise fill it corner to
     corner. */
  @media (max-width: 800px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

/**
 * The round white control that floats over dark media: the lightbox's close
 * and arrows, the image editor's close and whole toolbar. (The video player's
 * corner buttons look related but are not this - they fade with the chrome.)
 * 12px is the right glyph size at 32px; anything bigger restates both.
 */
export const RoundButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

/** The same field, single line, with the magnifier where the icons would be. */
export const SearchBar = styled.div`
  ${composerSurface}
  align-items: center;
  padding-right: 12px;

  svg {
    flex: none;
    display: block;
  }
`;

export const SearchField = styled.input`
  ${composerText}
`;

export const ComposerTextarea = styled.textarea`
  ${composerText}
  resize: none;
  max-height: 120px;

  /*
   * Grows to the height of the phone's 40px buttons rather than staying the
   * 32px the desktop line box needs.
   *
   * The bar aligns its children by their BOTTOM edge, so a short field and a
   * tall button do not meet in the middle: the draft sat four pixels under the
   * icons' centre line, which reads as a typo in the layout. Padding it to the
   * same 40px puts the first line back on their axis, and the bar keeps
   * growing from there as the draft wraps.
   */
  @media (max-width: 800px) {
    padding: 10px 0;
  }
`;

/**
 * Quoted strip above the composer, and inside a bubble for `replyTo`.
 *
 * The accent is a `<StatusMark />` - the same rounded bar the order and
 * transaction rows use - rather than a `border-left`, so the chat picks up the
 * marker the rest of the admin already speaks in. It is absolutely positioned,
 * hence `position: relative`; the 12px left padding clears its 6px width and
 * leaves the text 6px off the marker.
 */
export const ReplyStrip = styled.div<{
  $inBubble?: boolean;
  $own?: boolean;
  $clickable?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  ${({ $clickable }) =>
    $clickable &&
    `
    cursor: pointer;
    :hover { filter: brightness(0.96); }
  `}
  margin-bottom: ${({ $inBubble }) => ($inBubble ? '8px' : '12px')};
  border-radius: 8px;
  overflow: hidden;
  /* A thin white veil inside your own red bubble; grey everywhere else - in an
     incoming bubble, and above the composer. Both are a quiet step off the
     surface they sit on, where one colour would vanish into one of them. */
  background: ${({ theme, $inBubble, $own }) =>
    $inBubble && $own ? `${theme.white}1A` : theme.gray};
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme, $inBubble, $own }) =>
    $inBubble && $own ? theme.white : theme.darkGray};
`;

/**
 * The two lines inside a reply strip: who wrote it, and what they said.
 *
 * Each is capped at ONE line and ellipsed. Above the composer that is what
 * keeps the strip a fixed height: a quoted message can be a wall of pasted
 * links, and at full height it grew taller than a phone screen and carried the
 * dismiss button off it - leaving no way out of the reply but sending it. In a
 * bubble the same cap keeps the quote from burying the message that answers it.
 *
 * `min-width: 0` is what makes the ellipsis possible at all: a flex item
 * defaults to `min-width: auto` and refuses to shrink below its content, so
 * without this the text pushes the strip wider instead of being cut.
 */
export const ReplyStripBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/**
 * The dismiss button on the composer's strip.
 *
 * Never shrinks, and never scrolls out of reach: the body beside it gives way
 * first, so this stays a tap target at the strip's right edge whatever was
 * quoted.
 */
export const ReplyStripClose = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

/**
 * The timestamp riding inside a self-contained attachment - the audio pill or
 * the file chip - where no bubble or caption can carry it.
 */
export const MediaMeta = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  color: ${({ theme }) => theme.lightGray};

  svg {
    display: block;
  }
`;
